/*
 * The actionable needs_human / recovery surface for a run detail page.
 *
 * Renders each persisted inconsistency row with its verdict badge, the
 * `recommended_action` the recovery engine surfaced verbatim, and an inline link
 * into the full report (U25). Unresolved discrepancies are emphasised (the
 * attention border) so an operator's eye goes to what still needs a human.
 *
 * Each row now also surfaces the enriched fields (concept name, expected vs
 * actual ad counts, and the per-status breakdown) so the operator can see what
 * is wrong without opening the full report.
 *
 * Driven by the inconsistency report query in the page; presentational here.
 */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button, Text } from "@mantine/core";
import type { InconsistencyRow } from "../../api/client";
import { StatusPill } from "../StatusPill";
import styles from "./pipeline.module.css";

/** Monospace inline cell helper for ids/timestamps. */
function Mono({ children }: { children: ReactNode }) {
  return (
    <Text component="span" ff="monospace" fz="sm">
      {children}
    </Text>
  );
}

export interface RecoveryItemsProps {
  runId: string;
  rows: InconsistencyRow[];
  recheckRecommended: boolean;
}

export function RecoveryItems({
  runId,
  rows,
  recheckRecommended,
}: RecoveryItemsProps) {
  const unresolved = rows.filter((r) => !r.resolved);

  return (
    <section className={styles.panel} aria-label="Recovery & manual review">
      <div className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>
          Needs review ({unresolved.length})
        </h2>
        <Link to={`/runs/${runId}/report`}>
          <Button size="xs" variant="subtle" color="gray">
            Full report →
          </Button>
        </Link>
      </div>

      {recheckRecommended && (
        <p className={styles.recoveryAction}>
          <span className={styles.recoveryActionLabel}>Re-audit:</span>
          Some ads were still <Mono>PENDING_REVIEW</Mono> at audit time — a
          recheck is scheduled (~1h) to confirm final delivery status.
        </p>
      )}

      {rows.length === 0 ? (
        <p className={styles.empty}>
          No discrepancies recorded for this run.
        </p>
      ) : (
        <ul className={styles.recoveryList}>
          {rows.map((row) => {
            const actionable = !row.resolved;

            // Build a readable status-breakdown string, e.g. "ACTIVE: 2, DISAPPROVED: 1"
            const breakdownText = row.status_breakdown
              ? Object.entries(row.status_breakdown)
                  .map(([k, v]) => `${k}: ${v as number}`)
                  .join(", ")
              : null;

            return (
              <li
                key={row.id}
                className={`${styles.recoveryItem} ${
                  actionable ? styles.recoveryItemActionable : ""
                }`}
              >
                <div className={styles.recoveryBody}>
                  <div className={styles.recoveryMeta}>
                    <StatusPill kind="verdict" value={row.classification} />
                    {/* Concept name is the primary human identifier — show it prominently */}
                    {row.concept ? (
                      <strong className={styles.recoveryConcept}>{row.concept}</strong>
                    ) : (
                      row.ticket_id && <Mono>{row.ticket_id.slice(0, 8)}</Mono>
                    )}
                    {row.resolved && (
                      <StatusPill tone="success" label="Resolved" hideDot />
                    )}
                  </div>

                  {/* Expected vs actual ad counts */}
                  {(row.expected_ads != null || row.actual_ads != null) && (
                    <div className={styles.recoveryDetail}>
                      <span className={styles.recoveryActionLabel}>Ad count:</span>
                      {row.expected_ads != null && row.actual_ads != null ? (
                        <>Expected {row.expected_ads} ads, found {row.actual_ads}</>
                      ) : row.expected_ads != null ? (
                        <>Expected {row.expected_ads} ads</>
                      ) : (
                        <>Found {row.actual_ads} ads</>
                      )}
                    </div>
                  )}

                  {/* Per-status breakdown */}
                  {breakdownText && (
                    <div className={styles.recoveryDetail}>
                      <span className={styles.recoveryActionLabel}>Breakdown:</span>
                      <Mono>{breakdownText}</Mono>
                    </div>
                  )}

                  {row.recommended_action && (
                    <div className={styles.recoveryAction}>
                      <span className={styles.recoveryActionLabel}>
                        Recommended:
                      </span>
                      {row.recommended_action}
                    </div>
                  )}
                </div>
                <div className={styles.recoverySide}>
                  <Link to={`/runs/${runId}/report`}>
                    <Button size="xs" variant="default">
                      Resolve
                    </Button>
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
